import {
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import AddRequestsFromHistoryDialog from "./AddRequestsFromHistoryDialog";
import ControlBar from "./ControlBar";
import DrawerContent from "./DrawerContent";
import {
  DRAWER_COLLAPSE_THRESHOLD,
  DRAWER_DEFAULT_HEIGHT,
  DRAWER_MAX_HEIGHT_RATIO,
  DRAWER_MIN_HEIGHT,
  DRAWER_PEEK_HEIGHT,
} from "./constants";
import { useSignalTestDrawer } from "./useSignalTestDrawer";

import { cn } from "@/lib/utils";

const getMaxDrawerHeight = () => {
  if (typeof window === "undefined") {
    return 760;
  }

  return Math.max(DRAWER_MIN_HEIGHT, Math.floor(window.innerHeight * DRAWER_MAX_HEIGHT_RATIO));
};

const clampExpandedDrawerHeight = (height: number) =>
  Math.min(Math.max(height, DRAWER_MIN_HEIGHT), getMaxDrawerHeight());

const clampDrawerHeight = (height: number) => {
  if (height <= DRAWER_COLLAPSE_THRESHOLD) {
    return DRAWER_PEEK_HEIGHT;
  }

  return clampExpandedDrawerHeight(height);
};

interface SignalTestDrawerProps {
  workspaceId: number;
  prompt: string;
}

const SignalTestDrawer = ({ workspaceId, prompt }: SignalTestDrawerProps) => {
  const initialHeight = clampExpandedDrawerHeight(DRAWER_DEFAULT_HEIGHT);
  const [height, setHeight] = useState(initialHeight);
  const [isDragging, setIsDragging] = useState(false);
  const [isRequestHistoryDialogOpen, setIsRequestHistoryDialogOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const lastExpandedHeightRef = useRef(initialHeight);
  const dragStartRef = useRef<{ y: number; height: number; hasMoved: boolean } | null>(null);

  const drawer = useSignalTestDrawer({ workspaceId, prompt });

  const isCollapsed = height === DRAWER_PEEK_HEIGHT;

  useEffect(() => {
    const handleResize = () => {
      setHeight(currentHeight => {
        if (currentHeight === DRAWER_PEEK_HEIGHT) {
          return DRAWER_PEEK_HEIGHT;
        }

        return clampExpandedDrawerHeight(currentHeight);
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isCollapsed) {
      lastExpandedHeightRef.current = height;
    }
  }, [height, isCollapsed]);

  const expandDrawer = useCallback(() => {
    if (!isCollapsed) {
      return;
    }

    setHeight(clampExpandedDrawerHeight(lastExpandedHeightRef.current));
  }, [isCollapsed]);

  const handleOpenRequestHistoryDialog = useCallback(() => {
    expandDrawer();
    setIsRequestHistoryDialogOpen(true);
  }, [expandDrawer]);

  const handleTestAll = async () => {
    expandDrawer();
    await drawer.handleTestAll();
  };

  const handlePointerMove = useCallback((event: PointerEvent) => {
    const dragStart = dragStartRef.current;
    if (!dragStart) {
      return;
    }

    if (Math.abs(event.clientY - dragStart.y) > 4) {
      dragStart.hasMoved = true;
    }

    const nextHeight = dragStart.height + dragStart.y - event.clientY;
    setHeight(clampDrawerHeight(nextHeight));
  }, []);

  const handlePointerUp = useCallback(() => {
    const dragStart = dragStartRef.current;

    if (dragStart && !dragStart.hasMoved) {
      setHeight(currentHeight =>
        currentHeight === DRAWER_PEEK_HEIGHT
          ? clampExpandedDrawerHeight(lastExpandedHeightRef.current)
          : DRAWER_PEEK_HEIGHT,
      );
    }

    dragStartRef.current = null;
    setIsDragging(false);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", handlePointerUp);
  }, [handlePointerMove]);

  useEffect(
    () => () => {
      setIsDragging(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    },
    [handlePointerMove, handlePointerUp],
  );

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragStartRef.current = { y: event.clientY, height, hasMoved: false };
    setIsDragging(true);
    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setHeight(currentHeight =>
        currentHeight === DRAWER_PEEK_HEIGHT
          ? clampExpandedDrawerHeight(lastExpandedHeightRef.current)
          : DRAWER_PEEK_HEIGHT,
      );
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHeight(currentHeight =>
        currentHeight === DRAWER_PEEK_HEIGHT
          ? clampExpandedDrawerHeight(lastExpandedHeightRef.current)
          : clampDrawerHeight(currentHeight + 24),
      );
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHeight(currentHeight => {
        if (currentHeight <= DRAWER_MIN_HEIGHT) {
          return DRAWER_PEEK_HEIGHT;
        }

        return clampDrawerHeight(currentHeight - 24);
      });
    }
  };

  return (
    <>
      <div className="relative z-10 w-full shrink-0 overflow-visible">
        <aside
          className={cn(
            "relative flex w-full flex-col overflow-hidden rounded-t-[18px] border border-b-0 border-gray-200 bg-white",
            "shadow-[0_-9px_20px_-16px_rgba(15,23,42,0.20)]",
            "transition-[height] duration-200 ease-out",
            isDragging && "transition-none",
            isCollapsed && "shadow-[0_-5px_12px_-12px_rgba(15,23,42,0.14)]",
          )}
          style={{ height }}
          aria-label="Signal test drawer"
        >
          <header
            ref={headerRef}
            className={cn(
              "relative shrink-0 bg-white/95 backdrop-blur",
              isCollapsed ? "h-full border-b-0" : "border-b border-gray-100",
            )}
          >
            <div
              className={cn(
                "group absolute left-1/2 top-1 z-10 flex h-5 w-16 -translate-x-1/2 cursor-ns-resize items-center justify-center rounded-md transition-colors duration-200",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500/40",
                isCollapsed && "top-0 h-4",
              )}
              onPointerDown={handlePointerDown}
              onKeyDown={handleKeyDown}
              role="separator"
              aria-orientation="horizontal"
              aria-label="Resize signal test drawer"
              aria-valuemin={DRAWER_PEEK_HEIGHT}
              aria-valuemax={getMaxDrawerHeight()}
              aria-valuenow={height}
              aria-valuetext={isCollapsed ? "Collapsed" : `${height}px`}
              tabIndex={0}
            >
              <span className="h-1 w-10 rounded-full bg-gray-300 transition-colors duration-200 group-hover:bg-gray-400" />
            </div>
            <ControlBar
              collapsed={isCollapsed}
              requestLogCount={drawer.requestLogs.length}
              hasAssertion={drawer.hasAssertion}
              isTestingAll={drawer.isTestingAll}
              isTestPending={drawer.isTestPending}
              hasTestActivity={drawer.hasTestActivity}
              testStats={drawer.testStats}
              onAddRequests={handleOpenRequestHistoryDialog}
              onTestAll={() => void handleTestAll()}
            />
          </header>

          {!isCollapsed && (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-gray-50/60 pt-2">
              <DrawerContent
                isLoading={drawer.isLoading}
                requestLogs={drawer.requestLogs}
                workspaceId={workspaceId}
                testStates={drawer.testStates}
                isTestingAll={drawer.isTestingAll}
                hasAssertion={drawer.hasAssertion}
                onRemoveRequest={drawer.removeRequestLog}
                onTestRequest={requestLogId => void drawer.handleTestRequest(requestLogId)}
              />
            </div>
          )}
        </aside>
      </div>

      <AddRequestsFromHistoryDialog
        open={isRequestHistoryDialogOpen}
        onOpenChange={setIsRequestHistoryDialogOpen}
        currentSampleCount={drawer.selectedRequestLogIds.length}
        onAddRequestLogs={drawer.addRequestLogs}
      />
    </>
  );
};

export default SignalTestDrawer;
